// src/components/hrms/RouteTrackingMap.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  useMap,
  CircleMarker,
} from "react-leaflet";
import L from "leaflet";

import "leaflet/dist/leaflet.css";
import marker2x from "leaflet/dist/images/marker-icon-2x.png";
import marker1x from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
L.Icon.Default.mergeOptions({
  iconRetinaUrl: marker2x,
  iconUrl: marker1x,
  shadowUrl: markerShadow,
});

// ---- ZOOM SETTINGS ----
const DEFAULT_ZOOM = 17;
const MAX_ZOOM = 19;
const FIT_MAX_ZOOM = 18;

/** Fit map to route bounds when geometry changes */
function FitTo({ coords }) {
  const map = useMap();
  useEffect(() => {
    if (!coords?.length) return;
    const bounds = L.latLngBounds(coords);
    map.fitBounds(bounds, {
      padding: [32, 32],
      maxZoom: FIT_MAX_ZOOM, // ✅ keep it nicely zoomed in for shop names
    });
  }, [coords, map]);
  return null;
}

/** Fetch route via OSRM public endpoint (with abort support) */
async function fetchOsrmRoute(fromLatLng, toLatLng, signal) {
  const [fromLat, fromLng] = fromLatLng;
  const [toLat, toLng] = toLatLng;
  const url =
    `https://router.project-osrm.org/route/v1/driving/` +
    `${fromLng},${fromLat};${toLng},${toLat}` +
    `?overview=full&geometries=geojson&steps=false`;
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`OSRM ${res.status}`);
  const json = await res.json();
  const route = json?.routes?.[0];
  if (!route) return null;
  return {
    coords: route.geometry.coordinates.map(([lng, lat]) => [lat, lng]),
    distanceM: route.distance,
    durationS: route.duration,
  };
}

export default function RouteTrackingMap({
  /** required */
  origin, // [lat,lng] e.g., office/store/customer
  courier, // [lat,lng] current moving employee position
  /** options */
  tiles = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
  attribution = '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>',
  recalcThresholdM = 50, // recompute route when courier moved > 50m
  pingEveryMs = 15000, // soft refresh route in case of traffic changes
  className = "h-full w-full rounded-2xl",
}) {
  const [route, setRoute] = useState(null); // {coords, distanceM, durationS}
  const prevCourierRef = useRef(null);
  const prevOriginRef = useRef(null);
  const [center, setCenter] = useState(courier || origin || [20, 78]); // India fallback
  const mountedRef = useRef(true);
  const inflightAbortRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (inflightAbortRef.current) {
        inflightAbortRef.current.abort();
        inflightAbortRef.current = null;
      }
    };
  }, []);

  // Great-circle distance (meters)
  const distanceMeters = useMemo(
    () => (a, b) => {
      if (!a || !b) return Infinity;
      const toRad = (d) => (d * Math.PI) / 180;
      const R = 6371000;
      const dLat = toRad(b[0] - a[0]);
      const dLng = toRad(b[1] - a[1]);
      const lat1 = toRad(a[0]);
      const lat2 = toRad(b[0]);
      const x =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
      return 2 * R * Math.asin(Math.sqrt(x));
    },
    []
  );

  // Determine if we need to recalc the route
  const needRecalc = useMemo(() => {
    const prevC = prevCourierRef.current;
    const prevO = prevOriginRef.current;
    // Recalc if first load, or origin changed, or courier moved enough
    if (!route) return true;
    if (!prevO || !origin || prevO[0] !== origin[0] || prevO[1] !== origin[1]) {
      return true;
    }
    if (!prevC || !courier) return false;
    const dist = distanceMeters(prevC, courier);
    return dist > recalcThresholdM;
  }, [route, courier, origin, distanceMeters, recalcThresholdM]);

  // initial and movement-based routing
  useEffect(() => {
    if (!origin || !courier) return;

    // cancel any in-flight request
    if (inflightAbortRef.current) {
      inflightAbortRef.current.abort();
    }
    const ctrl = new AbortController();
    inflightAbortRef.current = ctrl;

    async function run() {
      try {
        const r = await fetchOsrmRoute(courier, origin, ctrl.signal); // route from courier -> origin (destination)
        if (!mountedRef.current) return;
        if (ctrl.signal.aborted) return;

        setRoute(r);
        setCenter(courier); // keep camera around courier
        prevCourierRef.current = courier;
        prevOriginRef.current = origin;
      } catch (err) {
        if (err?.name === "AbortError") return;
        // ignore transient OSRM errors
      } finally {
        if (inflightAbortRef.current === ctrl) {
          inflightAbortRef.current = null;
        }
      }
    }

    if (needRecalc) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin, courier, needRecalc]);

  // periodic soft refresh (keeps ETA fresh)
  useEffect(() => {
    if (!origin || !courier) return;
    const id = setInterval(async () => {
      if (inflightAbortRef.current) return;
      const ctrl = new AbortController();
      inflightAbortRef.current = ctrl;
      try {
        const r = await fetchOsrmRoute(courier, origin, ctrl.signal);
        if (!mountedRef.current || ctrl.signal.aborted) return;
        setRoute(r);
      } catch (err) {
        // ignore transient errors
      } finally {
        if (inflightAbortRef.current === ctrl) {
          inflightAbortRef.current = null;
        }
      }
    }, Math.max(5000, pingEveryMs));
    return () => clearInterval(id);
  }, [origin, courier, pingEveryMs]);

  const prettyKm = (m) =>
    Number.isFinite(m)
      ? m >= 1000
        ? `${(m / 1000).toFixed(1)} km`
        : `${Math.round(m)} m`
      : "—";
  const prettyEta = (s) => {
    if (!Number.isFinite(s)) return "—";
    const min = Math.round(s / 60);
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    const rem = min % 60;
    return `${h}h ${rem}m`;
  };

  return (
    <div className="relative h-full w-full">
      {/* Info chip like Swiggy/Zomato */}
      <div className="absolute left-3 top-3 z-[500] rounded-lg bg-white/90 px-3 py-2 text-xs shadow">
        <div className="font-semibold">On the way</div>
        <div>
          <span className="text-gray-700">ETA:</span>{" "}
          <span className="font-medium">
            {route ? prettyEta(route.durationS) : "calculating…"}
          </span>
        </div>
        <div>
          <span className="text-gray-700">Distance:</span>{" "}
          <span className="font-medium">
            {route ? prettyKm(route.distanceM) : "—"}
          </span>
        </div>
      </div>

      <MapContainer
        center={center}
        zoom={DEFAULT_ZOOM}
        maxZoom={MAX_ZOOM}
        className={className}
        scrollWheelZoom
      >
        <TileLayer url={tiles} attribution={attribution} />

        {/* Destination pin (office/store) */}
        {origin && (
          <CircleMarker
            center={origin}
            radius={8}
            pathOptions={{ color: "#10b981", weight: 3, opacity: 0.9 }}
          />
        )}

        {/* Courier pin */}
        {courier && <Marker position={courier} />}

        {/* Route polyline */}
        {route?.coords?.length ? (
          <>
            <Polyline positions={route.coords} weight={5} opacity={0.9} />
            <FitTo coords={route.coords} />
          </>
        ) : null}
      </MapContainer>
    </div>
  );
}
